var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { boundClass } from 'autobind-decorator';
import { ajaxSend } from '../../../api/api';
let CsatApi = class CsatApi {
    loadSettings(params) {
        return ajaxSend({
            path: '/v2/ratings/settings',
            method: 'GET',
            params: { level: params.level, channel: params.channel },
        });
    }
    /**
     * Here we are trying to fetch settings for all levels but the reason why we
     * can't make it one api call is that this.loadContactLevelSettings is not a
     * simple api call at this moment. to find out why read the comments on
     * loadContactLevelSettings method. When - in the future - the limitations of
     * contact level settings api are lifted this api call can be replaced with
     * simply calling this.loadSettings().
     */
    loadSettingForAllLevels(channel) {
        return __awaiter(this, void 0, void 0, function* () {
            const [globalSettings, contactLevelSettings, languageLevelSettings] = yield Promise.all([
                this.loadGlobalSettings(channel),
                this.loadContactLevelSettings(channel),
                this.loadLanguageLevelSettings(channel),
            ]);
            return {
                globalSettings,
                contactLevelSettings,
                languageLevelSettings,
            };
        });
    }
    //
    // ─── GLOBAL SETTINGS ────────────────────────────────────────────────────────────
    //
    initializeCsat(channel) {
        return ajaxSend({
            path: '/v2/ratings/settings/initialize',
            method: 'POST',
            data: {
                channel,
            },
        });
    }
    loadGlobalSettings(channel) {
        return this.loadSettings({ level: 'channel', channel });
    }
    saveGlobalSettings(data, meta) {
        if (meta === null || meta === void 0 ? void 0 : meta.edit) {
            return this.updateGlobalSettings(data);
        }
        return this.createGlobalSettings(data);
    }
    createGlobalSettings(data) {
        return ajaxSend({
            path: '/v2/ratings/settings',
            method: 'POST',
            data,
        });
    }
    updateGlobalSettings(data) {
        return ajaxSend({
            path: `/v2/ratings/settings/${data.id}`,
            method: 'PUT',
            data,
        });
    }
    //
    // ─── CONTACT LEVEL SETTINGS ─────────────────────────────────────────────────────
    //
    /**
     * In the MVP version of the csat for email we are not allowing for dedicated
     * settings for each contact point (email). but this capability already exists
     * in our backend. In the MVP we have only ONE contact-point-level settings,
     * and just link all the contact points to that settings. In the future we can
     * have as many contact-point-level settings as we want. but to make future
     * development easier here we "simulate" having multiple contact-level
     * settings by fetching the list of linked contact points to the only
     * contact-level-setting there is, and just copy the contact-level-setting
     * into the list of emails.
     *
     * Only email and dixa messenger have contact level settings
     */
    loadContactLevelSettings(channel) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const masterContactLevelSetting = yield this.loadTheMasterContactLevelSetting(channel);
                const response = yield this.loadContactLevelSettingsLinks(masterContactLevelSetting.id);
                // enrich each setting with the contactPoint field
                const contactPointSettings = response.data.contact_points.map((contactPoint) => (Object.assign(Object.assign({}, masterContactLevelSetting), { contactPoint: contactPoint })));
                return {
                    channel,
                    data: {
                        rating_settings: contactPointSettings,
                    },
                    meta: { next_page: null, prev_page: null },
                };
            }
            catch (error) {
                return {
                    channel,
                    data: { rating_settings: [] },
                    meta: { next_page: null, prev_page: null },
                };
            }
        });
    }
    loadTheMasterContactLevelSetting(channel) {
        return __awaiter(this, void 0, void 0, function* () {
            // load the contact level settings and since there is only one setting for all of the contact points
            // for now (it will probably change in the future) we assume that the first setting is THE setting.
            const { data: { rating_settings: [parentSettings], }, } = yield this.loadSettings({ level: 'contact_point', channel });
            if (!parentSettings) {
                return Promise.reject(new Error('There was no contact-level-settings defined'));
            }
            return parentSettings;
        });
    }
    // Only email and dixa messenger have contact level settings
    /* eslint-disable consistent-return */
    saveContactLevelSettings(payload, parentSetting, channel) {
        return __awaiter(this, void 0, void 0, function* () {
            // In the future we can have independent settings for each contact point.
            // but for now we have only one settings for all of them
            // each contact point is either linked to that setting or not.
            // For now there is no way to save settings at the contact point level for chat
            if (channel === 'widgetchat') {
                throw new Error('"widgetchat" does not support contact-level settings ');
            }
            const contactPointIdentifier = channel === 'email'
                ? payload.emailAddress
                : payload.id;
            try {
                // Only want to link contact point if there is an identifier.
                if (payload.newStatus === true) {
                    if (contactPointIdentifier) {
                        yield this.linkContactPointToSetting(parentSetting, contactPointIdentifier, channel);
                    }
                    return this.loadContactLevelSettings(channel);
                }
                if (payload.newStatus === false) {
                    // Only want to unlink contact point if there is an identifier
                    if (contactPointIdentifier) {
                        yield this.unlinkContactPointFromSetting(parentSetting, contactPointIdentifier);
                    }
                    return this.loadContactLevelSettings(channel);
                }
                // the new state was not true or false so it's not clear what is expected.
                console.error('Bad input', payload, parentSetting);
                throw new Error('CsatApi.saveContactLevelSettings received a payload that is not acceptable');
            }
            catch (error) {
                return Promise.reject(error);
            }
        });
    }
    //
    // ─── LINKS BETWEEN SETTING AND CONTACT POINT ────────────────────────────────────
    //
    loadContactLevelSettingsLinks(contactLevelSettingId) {
        return ajaxSend({
            path: `/v2/ratings/settings/${contactLevelSettingId}/links`,
            method: 'GET',
        });
    }
    saveContactLevelSettingsLinks(contactLevelSettingId, contactPoint, operation) {
        return ajaxSend({
            path: `/v2/ratings/settings/${contactLevelSettingId}/links`,
            method: operation === 'DELETE_LINK' ? 'DELETE' : 'POST',
            data: {
                contact_point: contactPoint,
            },
        });
    }
    linkContactPointToSetting(setting, contactPointIdentifier, channel) {
        return __awaiter(this, void 0, void 0, function* () {
            // we need to link this contact point to the only existing contact-point-level setting.
            if (setting === null || setting === void 0 ? void 0 : setting.id) {
                // it is already linked to a THE setting.
                return Promise.reject();
            }
            // since there's no parent setting, we use the master settings.
            try {
                setting = yield this.loadTheMasterContactLevelSetting(channel);
            }
            catch (error) {
                console.error('failed to link email, because no setting is defined');
                return Promise.reject(error);
            }
            yield this.saveContactLevelSettingsLinks(setting.id, contactPointIdentifier, 'ADD_LINK');
            return Promise.resolve();
        });
    }
    unlinkContactPointFromSetting(setting, contactPointIdentifier) {
        return __awaiter(this, void 0, void 0, function* () {
            // trying to unlink a contact point from a setting
            if (!(setting === null || setting === void 0 ? void 0 : setting.id)) {
                // it's not liked to a setting already! so we don't have to do anything
                return Promise.reject();
            }
            // save the new list of contact points to be linked with the parent settings.
            yield this.saveContactLevelSettingsLinks(setting.id, contactPointIdentifier, 'DELETE_LINK');
            return Promise.resolve();
        });
    }
    //
    // ─── LANGUAGE LEVEL SETTINGS ────────────────────────────────────────────────────
    //
    loadLanguageLevelSettings(channel) {
        return this.loadSettings({ level: 'language', channel });
    }
    saveLanguageLevelSettings(settings) {
        return __awaiter(this, void 0, void 0, function* () {
            if (settings.id) {
                // There already is a setting for this language, so we just update it.
                return ajaxSend({
                    path: `/v2/ratings/settings/${settings.id}`,
                    method: 'PUT',
                    data: settings,
                });
            }
            /**
             * We need to create new language level setting. but it can't be done
             * without having a parent setting due to the hierarchical design of the
             * settings in the backend. for now, we have only ONE email-level setting
             * instance that all emails use (MVP version). So we have to choose that one
             * as the parent setting for all languages-level settings.
             *
             * It is likely that in the future each email will have it's own setting and
             * naturally a it's own language settings as it's child settings.
             *
             * Note that child settings override parent settings and parent settings act
             * as the fallback for child setting.
             */
            if (settings.channel) {
                const parentSettings = yield this.loadTheMasterContactLevelSetting(settings.channel);
                return ajaxSend({
                    path: `/v2/ratings/settings`,
                    method: 'POST',
                    data: Object.assign(Object.assign({}, settings), { parent_id: parentSettings.id }),
                });
            }
            return Promise.reject();
        });
    }
};
CsatApi = __decorate([
    boundClass
], CsatApi);
export const csatApi = new CsatApi();
